/**
 * EventBus - Global event system (replaces Godot signals)
 * Uses Phaser's EventEmitter for type-safe events
 */

import Phaser from 'phaser';

// Event names as constants for type safety
export const GameEvents = {
  // Phase/Turn events
  PHASE_CHANGED: 'phase-changed',
  TURN_ENDED: 'turn-ended',
  PHASE_TIMER_UPDATE: 'phase-timer-update',

  // Entity events
  UNIT_SELECTED: 'unit-selected',
  UNIT_DESELECTED: 'unit-deselected',
  UNIT_MOVED: 'unit-moved',
  UNIT_ATTACKED: 'unit-attacked',
  UNIT_DESTROYED: 'unit-destroyed',
  UNIT_CREATED: 'unit-created',

  PLANET_SELECTED: 'planet-selected',
  PLANET_DESELECTED: 'planet-deselected',
  PLANET_CAPTURED: 'planet-captured',
  PLANET_ATTACKED: 'planet-attacked',

  ASTEROID_SELECTED: 'asteroid-selected',
  ASTEROID_CAPTURED: 'asteroid-captured',

  // Resource events
  RESOURCES_CHANGED: 'resources-changed',
  INCOME_COLLECTED: 'income-collected',

  // Trade ship events
  TRADE_CARGO_LOADED: 'trade-cargo-loaded',
  TRADE_CARGO_DELIVERED: 'trade-cargo-delivered',
  TRADE_ROUTE_ASSIGNED: 'trade-route-assigned',

  // Tech events
  TECH_RESEARCHED: 'tech-researched',
  TECH_CHOICE_AVAILABLE: 'tech-choice-available',

  // Building events
  BUILDING_CONSTRUCTED: 'building-constructed',

  // Fleet events
  FLEET_FORMED: 'fleet-formed',
  FLEET_DISBANDED: 'fleet-disbanded',

  // Combat events
  COMBAT_STARTED: 'combat-started',
  COMBAT_ENDED: 'combat-ended',
  DAMAGE_DEALT: 'damage-dealt',

  // Network events
  PLAYER_JOINED: 'player-joined',
  PLAYER_LEFT: 'player-left',
  PLAYER_READY: 'player-ready',
  GAME_STARTED: 'game-started',
  GAME_ENDED: 'game-ended',
  CONNECTION_LOST: 'connection-lost',
  CONNECTION_RESTORED: 'connection-restored',

  // UI events
  HEX_CLICKED: 'hex-clicked',
  HEX_HOVERED: 'hex-hovered',
  ENTITY_CLICKED: 'entity-clicked',
  CAMERA_MOVED: 'camera-moved',
  ZOOM_CHANGED: 'zoom-changed',
  DIALOG_OPENED: 'dialog-opened',
  DIALOG_CLOSED: 'dialog-closed',

  // Competitive events
  MATCH_FOUND: 'match-found',
  MATCH_STARTED: 'match-started',
  MATCH_ENDED: 'match-ended',
  QUEUE_JOINED: 'queue-joined',
  QUEUE_LEFT: 'queue-left',

  // Game log events
  LOG_MESSAGE: 'log-message',

  // Settings/Menu events
  GAME_PAUSED: 'game-paused',
  GAME_RESUMED: 'game-resumed',
  SETTINGS_CHANGED: 'settings-changed',
} as const;

export type GameEventType = (typeof GameEvents)[keyof typeof GameEvents];

// Create and export the singleton EventBus
export const EventBus = new Phaser.Events.EventEmitter();

// Expose on window for browser automation / debugging
(window as any).__EventBus = EventBus;

// Type-safe emit helper
export function emit<T = unknown>(event: GameEventType, data?: T): void {
  EventBus.emit(event, data);
}

// Type-safe on helper
export function on<T = unknown>(event: GameEventType, callback: (data: T) => void, context?: object): void {
  EventBus.on(event, callback, context);
}

// Type-safe once helper
export function once<T = unknown>(event: GameEventType, callback: (data: T) => void, context?: object): void {
  EventBus.once(event, callback, context);
}

// Type-safe off helper
export function off(event: GameEventType, callback?: (...args: unknown[]) => void, context?: object): void {
  EventBus.off(event, callback, context);
}
